if (!customElements.get('free-shipping-bar')) {
  // Shared cart total updated by any instance on cart:update, so newly connected
  // instances (from re-rendered step templates) don't read the stale server-rendered
  // data-total value baked in at page load.
  let _liveCartTotal = null;

  class FreeShippingBar extends HTMLElement {
    connectedCallback() {
      this._threshold      = parseInt(this.dataset.threshold, 10) || 0;
      const _currency = window.Shopify?.currency?.active;
      const _rate     = parseFloat(window.Shopify?.currency?.rate) || 1;
      if (_currency && _currency !== 'EUR' && _rate !== 1) {
        this._threshold = Math.round(this._threshold * _rate);
      }
      this._moneyFormat    = this.dataset.moneyFormat || '{{amount}}';
      this._tProgress      = this.dataset.tProgress      || 'Spend <strong>__AMOUNT__</strong> to get <strong>FREE</strong> shipping';
      this._tAchieved      = this.dataset.tAchieved      || "You've unlocked <strong>FREE</strong> shipping!";
      this._tProgressShort = this.dataset.tProgressShort || this._tProgress;
      this._tAchievedShort = this.dataset.tAchievedShort || this._tAchieved;
      this._bundleMode      = this.dataset.bundleMode === 'true';
      this._cartTotalCents  = _liveCartTotal ?? (parseInt(this.dataset.total, 10) || 0);
      this._lastExtraCents  = 0;

      this._update(this._cartTotalCents);

      this._onCartUpdate = this._onCart.bind(this);
      document.addEventListener('cart:update', this._onCartUpdate);

      if (this._bundleMode) {
        this._onResize = () => this._update(this._cartTotalCents + this._lastExtraCents);
        window.addEventListener('resize', this._onResize, { passive: true });
      }
    }

    disconnectedCallback() {
      document.removeEventListener('cart:update', this._onCartUpdate);
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    _onCart(event) {
      const cartFromEvent = event?.detail?.resource;
      if (cartFromEvent?.total_price != null) {
        this._cartTotalCents = cartFromEvent.total_price;
        _liveCartTotal = this._cartTotalCents;
        if (!this._locked) {
          this._update(this._cartTotalCents + (this._bundleMode ? this._lastExtraCents : 0));
        }
        return;
      }
      fetch('/cart.js')
        .then((r) => r.json())
        .then((cart) => {
          this._cartTotalCents = cart.total_price || 0;
          _liveCartTotal = this._cartTotalCents;
          if (!this._locked) {
            this._update(this._cartTotalCents + (this._bundleMode ? this._lastExtraCents : 0));
          }
        })
        .catch((e) => console.warn('[FSB] Could not fetch cart:', e));
    }

    lock()   { this._locked = true; }
    unlock() {
      this._locked = false;
      this._update(this._cartTotalCents + (this._bundleMode ? this._lastExtraCents : 0));
    }

    _isMobile() {
      return window.innerWidth <= 1130;
    }

    _texts() {
      if (this._bundleMode && this._isMobile()) {
        return { progress: this._tProgressShort, achieved: this._tAchievedShort };
      }
      return { progress: this._tProgress, achieved: this._tAchieved };
    }

    _update(totalCents) {
      if (!this._threshold) return;
      const remaining = Math.max(0, this._threshold - totalCents);
      const pct       = Math.min(100, (totalCents / this._threshold) * 100);
      const achieved  = totalCents >= this._threshold;

      const fill    = this.querySelector('.js-fsb-fill');
      const message = this.querySelector('.js-fsb-message');

      if (fill) fill.style.width = pct + '%';

      if (message) {
        const t = this._texts();
        if (achieved) {
          message.innerHTML = t.achieved;
        } else {
          message.innerHTML = t.progress.replace('__AMOUNT__', this._formatMoney(remaining));
        }
      }

      this.classList.toggle('free-shipping-bar--achieved', achieved);
    }

    _formatMoney(cents) {
      const amount = (cents / 100).toFixed(2);
      return this._moneyFormat
        .replace('{{amount}}', amount)
        .replace('{{amount_no_decimals}}', Math.floor(cents / 100))
        .replace('{{amount_with_comma_separator}}', amount.replace('.', ','))
        .replace('{{amount_no_decimals_with_comma_separator}}', String(Math.floor(cents / 100)));
    }

    updateWithExtra(extraCents) {
      this._lastExtraCents = extraCents;
      this._update(this._cartTotalCents + extraCents);
    }
  }

  customElements.define('free-shipping-bar', FreeShippingBar);
}
